import { ChangeEvent, type JSX, useCallback, useEffect, useState } from 'react';

import { Box, FormControl, Stack, Switch, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { EditDrawer } from 'common/components/EditDrawer';
import { selectNameInConflict, setSaveButtonEnabled } from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { ErrorHandler } from 'common/components/errorHandler/ErrorHandler';
import { getOptionLabelDefaultCallback, isOptionEqualToValueDefaultCallback } from 'common/components/sharedCustomComponents/Common/helpers';
import { EntitiesSearchableCommonDropDown } from 'common/components/sharedCustomComponents/EntitiesSearchableCommonDropDown/EntitiesSearchableCommonDropDown';
import { PalmDatePicker } from 'common/components/sharedCustomComponents/PalmDatePicker/PalmDatePicker';
import { ReadOnlyTextField } from 'common/components/sharedCustomComponents/ReadOnlyTextField/ReadOnlyTextField';
import { UserProfileSearchDropdown } from 'common/components/sharedCustomComponents/userProfileSearchDropdown/UserProfileSearchDropdown';
import { fetchAclResources } from 'common/services/api/Acls/AclResources.thunk';
import { fetchLicenses } from 'common/services/api/Licenses/Licenses.thunk';
import { FormattedPackageAssignment } from 'common/services/api/ProductPackages/PackageAssignments.thunk';
import { fetchProductPackageRals } from 'common/services/api/ProductPackages/ProductPackageRals.thunk';
import { fetchProductPackages } from 'common/services/api/ProductPackages/ProductPackages.thunk';
import { fetchUserGroups } from 'common/services/api/UserGroups/UserGroups.thunk';
import { IUser } from 'common/services/api/Users/Users.api';
import { EntitiesStatus, LicenseFormatted, PrincipalType, ProductPackage, UserGroupDropdownOption } from 'common/types/Entities';
import { displayEntityMenuItemContent, filterUserGroupOptionsCustomCallback } from 'common/utils/AclEntitiesDropDownHelpers';
import { dateToBackendFormat, IDatesValidation, toESTDateString, validateDates } from 'common/utils/dateHelpers';
import { AclConditionsCreateDropDown, AclConditionsEditDropDown } from 'features/aclConditions/AclConditionsDropDown';
import { CONDITION_VALIDATORS } from 'features/aclConditions/aclConditionsValidation';

import { PackageAssignmentDrawerProperties, PackageAssignmentPage } from './productPackageAssignmentTypeDefs';

type FormattedProductPackage = ProductPackage & { id: string; name: string };

export interface CreateStateHandlerResult {
    currentPackage: FormattedProductPackage | null;
    currentLicense: LicenseFormatted | null;
    currentConditionKey: string;
    currentConditionValue: Record<string, Record<string, string>>;
    startDate?: string;
    endDate?: string;
    datesValidation: IDatesValidation;
    principalType: PrincipalType;
    currentUser: IUser | null;
    currentUserGroup: UserGroupDropdownOption | null;
    onOpenChange: () => void;
    getOptions: () => object;
    getContent: (opCallback: () => void, processing: boolean) => JSX.Element;
}

export interface EditStateHandlerResult {
    currentConditionValues: Record<string, Record<string, string>>;
    startDate?: string;
    endDate?: string;
    datesValidation: IDatesValidation;
    onOpenChange: (isOpen: boolean) => void;
    getOptions: () => object;
    getContent: (opCallback: () => void, processing: boolean) => JSX.Element;
}

export function useCreateStateHandler(
    props: PackageAssignmentDrawerProperties
): CreateStateHandlerResult {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const nameInConflict = useAppSelector(selectNameInConflict) ?? false;

    const [currentPackage, setCurrentPackage] = useState<FormattedProductPackage | null>(null);
    const [currentLicense, setCurrentLicense] = useState<LicenseFormatted | null>(null);
    const [currentConditionKey, setCurrentConditionKey] = useState<string>('');
    const [currentConditionValue, setCurrentConditionValue] = useState<Record<string, Record<string, string>>>({});
    const [startDate, setStartDate] = useState<string | undefined>();
    const [endDate, setEndDate] = useState<string | undefined>();
    const [datesValidation, setDatesValidation] = useState<IDatesValidation>({ hasError: false });
    const [packageConditions, setPackageConditions] = useState<string[]>([]);
    const [principalType, setPrincipalType] = useState<PrincipalType>(PrincipalType.UserGroup);
    const [currentUser, setCurrentUser] = useState<IUser | null>(null);
    const [currentUserGroup, setCurrentUserGroup] = useState<UserGroupDropdownOption | null>(null);

    useEffect(() => {
        if (props.tenantRequiresLicense) {
            setDatesValidation(validateDates(startDate, endDate, true));
        }
    }, [startDate, endDate, props.tenantRequiresLicense]);

    const handleConditionKeyChange = useCallback((key: string) => {
        setCurrentConditionKey(key);
        setCurrentConditionValue({});
    }, []);

    const isConditionValid = useCallback((): boolean => {
        if (packageConditions.length === 0) return true;
        if (!currentConditionKey) return false;

        const conditionValue = currentConditionValue[currentConditionKey];
        const hasValue = conditionValue !== undefined &&
            Object.values(conditionValue).some(val => val !== undefined && val !== '');
        if (!hasValue) return false;

        const validator = CONDITION_VALIDATORS[currentConditionKey];
        if (validator) {
            return validator.validate(conditionValue?.[validator.parameterName]);
        }
        return true;
    }, [packageConditions.length, currentConditionKey, currentConditionValue]);

    const handleSaveBtnEnabled = useCallback(() => {
        let enabled = !!currentPackage?.productPackageId && isConditionValid();
        if (props.tenantRequiresLicense) {
            enabled &&= !datesValidation.hasError;
            if (props.page !== PackageAssignmentPage.License) {
                enabled &&= !!currentLicense?.id;
            }
        }
        if (props.page === PackageAssignmentPage.License) {
            if (principalType === PrincipalType.User) {
                enabled &&= !!currentUser?.id;
            } else {
                enabled &&= !!currentUserGroup?.id;
            }
        }
        dispatch(setSaveButtonEnabled(enabled));
    }, [
        dispatch,
        currentPackage?.productPackageId,
        currentLicense?.id,
        props.tenantRequiresLicense,
        props.page,
        datesValidation.hasError,
        isConditionValid,
        principalType,
        currentUser?.id,
        currentUserGroup?.id,
    ]);

    useEffect(() => {
        handleSaveBtnEnabled();
    }, [currentPackage, currentLicense, currentConditionKey, currentConditionValue, datesValidation, principalType, currentUser, currentUserGroup, handleSaveBtnEnabled]);

    const onOpenChange = useCallback(() => {
        setCurrentPackage(null);
        setCurrentLicense(null);
        setCurrentConditionKey('');
        setCurrentConditionValue({});
        setPackageConditions([]);
        setPrincipalType(PrincipalType.UserGroup);
        setCurrentUser(null);
        setCurrentUserGroup(null);
        if (props.tenantRequiresLicense) {
            const today = dateToBackendFormat(toESTDateString(new Date()));
            setStartDate(today);
        } else {
            setStartDate(undefined);
        }
        setEndDate(undefined);
        setDatesValidation({ hasError: false });
    }, [props.tenantRequiresLicense]);

    const handlePackageSelect = useCallback((_e: unknown, pkg: FormattedProductPackage | null) => {
        setCurrentPackage(pkg);
        setCurrentConditionKey('');
        setCurrentConditionValue({});
        setPackageConditions([]);

        if (!pkg?.productPackageId) return;

        dispatch(fetchProductPackageRals({ tenantId: props.tenantId, productPackageId: pkg.productPackageId }))
            .unwrap()
            .then((rals) => {
                if (!rals.length) return;
                dispatch(fetchAclResources({ tenantId: props.tenantId }))
                    .unwrap()
                    .then((resources) => {
                        const ralResourceIds = new Set(rals.map(r => r.aclResourceId));
                        const conditions = resources
                            .filter(r => ralResourceIds.has(r.id) && r.conditions && r.conditions.length > 0)
                            .flatMap(r => r.conditions as string[]);
                        const uniqueConditions = [...new Set(conditions)];
                        setPackageConditions(uniqueConditions);
                    })
                    .catch(() => setPackageConditions([]));
            })
            .catch(() => setPackageConditions([]));
    }, [dispatch, props.tenantId]);

    const handleLicenseSelect = useCallback((_e: unknown, license: LicenseFormatted | null) => {
        setCurrentLicense(license);
    }, []);

    const handleUserSelect = useCallback((_e: unknown, user: IUser | null) => {
        setCurrentUser(user ? {
            id: user.id,
            profile: {
                firstName: user.profile.firstName,
                lastName: user.profile.lastName,
                principalName: user.profile.principalName,
                email: user.profile.email,
                companyKey: user.profile.companyKey,
            }
        } : null);
    }, []);

    const handleUserGroupSelect = useCallback((_e: unknown, userGroup: UserGroupDropdownOption | null) => {
        setCurrentUserGroup(userGroup);
    }, []);

    const principalTypeChangeHandler = useCallback((_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setPrincipalType(checked ? PrincipalType.User : PrincipalType.UserGroup);
        setCurrentUser(null);
        setCurrentUserGroup(null);
    }, []);

    const getOptions = useCallback((): object => {
        const base: Record<string, unknown> = {
            tenantId: props.tenantId,
            productPackageId: currentPackage?.productPackageId ?? '',
        };

        if (props.page === PackageAssignmentPage.UserGroup) {
            base.userGroupId = props.principalId;
        } else if (props.page === PackageAssignmentPage.User || props.page === PackageAssignmentPage.UserProfile) {
            base.userId = props.principalId;
        } else if (props.page === PackageAssignmentPage.License) {
            if (principalType === PrincipalType.User) {
                base.userId = currentUser?.id;
            } else {
                base.userGroupId = currentUserGroup?.id;
            }
        }

        if (props.tenantRequiresLicense) {
            base.licenseId = props.page === PackageAssignmentPage.License ? props.licenseId : currentLicense?.id;
            base.startDate = startDate;
            if (endDate) base.endDate = endDate;
        }

        if (currentConditionKey && currentConditionValue[currentConditionKey]) {
            base.conditions = [{
                conditionKey: currentConditionKey,
                parameters: currentConditionValue[currentConditionKey],
            }];
        }

        return { ...base, page: props.page };
    }, [
        props,
        currentPackage,
        currentLicense,
        currentConditionKey,
        currentConditionValue,
        startDate,
        endDate,
        principalType,
        currentUser,
        currentUserGroup,
    ]);

    const getContent = useCallback((postCallback: () => void, processing: boolean): JSX.Element =>
        <EditDrawer title='Product Package Assignment' saveButtonFunction={postCallback} processing={processing}>
            <Stack spacing={2}>
                <EntitiesSearchableCommonDropDown
                    initialEntityOption={{ id: '', name: '', tenantId: props.tenantId, productPackageId: '', packageName: '', packageNote: '' } as FormattedProductPackage}
                    value={currentPackage}
                    onChange={handlePackageSelect}
                    label='Product Package'
                    getOptionLabel={(opt: FormattedProductPackage) => opt.packageName || ''}
                    isOptionEqualToValue={(opt: FormattedProductPackage, val: FormattedProductPackage) => opt.productPackageId === val.productPackageId}
                    asyncThunkAction={fetchProductPackages}
                    asyncThunkArgs={{ tenantId: props.tenantId }}
                    displayMenuItemContent={(opt: FormattedProductPackage) => <span>{opt.packageName}</span>}
                    effectDependecyArray={[props.tenantId]}
                />
                {packageConditions.length > 0 && (
                    <AclConditionsCreateDropDown
                        conditions={packageConditions}
                        currentConditionKey={currentConditionKey}
                        setCurrentConditionKey={handleConditionKeyChange}
                        currentConditionValue={currentConditionValue}
                        setCurrentConditionValue={setCurrentConditionValue}
                    />
                )}
                {props.tenantRequiresLicense && props.page !== PackageAssignmentPage.License && (
                    <EntitiesSearchableCommonDropDown
                        initialEntityOption={{ name: '', id: '', tenantId: '', companyKey: '', note: '', startDate: '', status: EntitiesStatus.INACTIVE } as LicenseFormatted}
                        value={currentLicense}
                        onChange={handleLicenseSelect}
                        label='License'
                        getOptionLabel={getOptionLabelDefaultCallback}
                        isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                        asyncThunkAction={fetchLicenses}
                        asyncThunkArgs={{ tenantId: props.tenantId }}
                        displayMenuItemContent={displayEntityMenuItemContent}
                        effectDependecyArray={[props.tenantId]}
                    />
                )}
                {props.tenantRequiresLicense && props.page === PackageAssignmentPage.License && (
                    <ReadOnlyTextField label='License' value={props.licenseName} />
                )}
                {props.tenantRequiresLicense && (
                    <>
                        <Stack direction='row' justifyContent='space-between' spacing={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <PalmDatePicker
                                    label='Start Date'
                                    required
                                    onChange={(value?: string) => setStartDate(value)}
                                    value={startDate}
                                />
                                <PalmDatePicker
                                    label='End Date'
                                    onChange={(value?: string) => setEndDate(value)}
                                    value={endDate}
                                />
                            </LocalizationProvider>
                        </Stack>
                        {datesValidation.hasError && (
                            <ErrorHandler component='div' display='inline' errorMessage={datesValidation.errorMessage} />
                        )}
                    </>
                )}
                {props.page === PackageAssignmentPage.License && (
                    <>
                        <FormControl sx={{ flexDirection: 'row', alignItems: 'center' }}>
                            <Typography sx={{ marginRight: '10px', fontWeight: 'bold' }}>Principal type:</Typography>
                            <Typography component='span' color={theme.palette.primary.accent}>User Group</Typography>
                            <Switch
                                sx={{
                                    '& .MuiSwitch-switchBase': {
                                        '&.Mui-checked': {
                                            '& + .MuiSwitch-track': { backgroundColor: theme.palette.primary.accent, opacity: 0.5 }
                                        }
                                    },
                                    '& .MuiSwitch-thumb': { backgroundColor: theme.palette.primary.accent, opacity: 1 },
                                    '& .MuiSwitch-track': { backgroundColor: theme.palette.primary.accent, opacity: 0.5 }
                                }}
                                value={principalType === PrincipalType.UserGroup}
                                onChange={principalTypeChangeHandler}
                            />
                            <Typography component='span' color={theme.palette.primary.accent}>User</Typography>
                        </FormControl>
                        {principalType === PrincipalType.User && (
                            <UserProfileSearchDropdown value={currentUser} onChange={handleUserSelect} hasError={false} />
                        )}
                        {principalType === PrincipalType.UserGroup && (
                            <EntitiesSearchableCommonDropDown
                                initialEntityOption={{ name: '', id: '', tenantId: props.tenantId, description: '' } as UserGroupDropdownOption}
                                value={currentUserGroup}
                                onChange={handleUserGroupSelect}
                                label='User Group'
                                getOptionLabel={getOptionLabelDefaultCallback}
                                isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                                asyncThunkAction={fetchUserGroups}
                                asyncThunkArgs={{ tenantId: props.tenantId }}
                                displayMenuItemContent={displayEntityMenuItemContent}
                                filterOptionsCustomCallback={filterUserGroupOptionsCustomCallback}
                                effectDependecyArray={[props.tenantId]}
                            />
                        )}
                    </>
                )}
                {props.page === PackageAssignmentPage.UserGroup && (
                    <ReadOnlyTextField label='User Group' value={props.principalName} />
                )}
                {(props.page === PackageAssignmentPage.User || props.page === PackageAssignmentPage.UserProfile) && (
                    <ReadOnlyTextField label='User' value={props.principalName} />
                )}
                <ReadOnlyTextField label='Tenant Name' value={props.tenantName} />
                {nameInConflict && (
                    <Box component='div' display='inline' sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                        Product Package Assignment already exists.
                    </Box>
                )}
            </Stack>
        </EditDrawer>,
    [
        props,
        theme,
        nameInConflict,
        currentPackage,
        currentLicense,
        currentConditionKey,
        currentConditionValue,
        packageConditions,
        startDate,
        endDate,
        datesValidation,
        principalType,
        currentUser,
        currentUserGroup,
        handlePackageSelect,
        handleLicenseSelect,
        handleConditionKeyChange,
        handleUserSelect,
        handleUserGroupSelect,
        principalTypeChangeHandler,
    ]);

    return {
        currentPackage,
        currentLicense,
        currentConditionKey,
        currentConditionValue,
        startDate,
        endDate,
        datesValidation,
        principalType,
        currentUser,
        currentUserGroup,
        onOpenChange,
        getOptions,
        getContent,
    };
}

export function useEditStateHandler(
    props: PackageAssignmentDrawerProperties,
    item: FormattedPackageAssignment | undefined
): EditStateHandlerResult {
    const dispatch = useAppDispatch();
    const nameInConflict = useAppSelector(selectNameInConflict) ?? false;

    const [currentConditionValues, setCurrentConditionValues] = useState<Record<string, Record<string, string>>>({});
    const [startDate, setStartDate] = useState<string | undefined>();
    const [endDate, setEndDate] = useState<string | undefined>();
    const [datesValidation, setDatesValidation] = useState<IDatesValidation>({ hasError: false });

    useEffect(() => {
        if (props.tenantRequiresLicense) {
            setDatesValidation(validateDates(startDate, endDate, true));
        }
    }, [startDate, endDate, props.tenantRequiresLicense]);

    const isConditionValid = useCallback((): boolean => {
        if (Object.keys(currentConditionValues).length === 0) return true;
        for (const conditionKey of Object.keys(currentConditionValues)) {
            const conditionValue = currentConditionValues[conditionKey];
            const hasValue = conditionValue !== undefined &&
                Object.values(conditionValue).some(val => val !== undefined && val !== '');
            if (!hasValue) return false;
            const validator = CONDITION_VALIDATORS[conditionKey];
            if (validator && !validator.validate(conditionValue?.[validator.parameterName])) return false;
        }
        return true;
    }, [currentConditionValues]);

    const handleSaveBtnEnabled = useCallback(() => {
        let enabled = !datesValidation.hasError && isConditionValid();
        if (props.tenantRequiresLicense && props.page !== PackageAssignmentPage.License) {
            enabled &&= !!item?.licenseId;
        }
        dispatch(setSaveButtonEnabled(enabled));
    }, [dispatch, datesValidation.hasError, props.tenantRequiresLicense, props.page, item?.licenseId, isConditionValid]);

    useEffect(() => {
        handleSaveBtnEnabled();
    }, [datesValidation, currentConditionValues, handleSaveBtnEnabled]);

    const onOpenChange = useCallback((isOpen: boolean) => {
        if (!item) return;

        setStartDate(dateToBackendFormat(item.startDate));
        setEndDate(item.endDate ? dateToBackendFormat(item.endDate) : undefined);
        setDatesValidation({ hasError: false });

        if (item.conditions && item.conditions.length > 0) {
            const initial: Record<string, Record<string, string>> = {};
            for (const condition of item.conditions) {
                initial[condition.conditionKey] = condition.parameters ?? {};
            }
            setCurrentConditionValues(initial);
        } else {
            setCurrentConditionValues({});
        }

        if (isOpen) {
            handleSaveBtnEnabled();
        }
    }, [item, handleSaveBtnEnabled]);

    const getOptions = useCallback((): object => {
        const base: Record<string, unknown> = {
            tenantId: item?.tenantId ?? props.tenantId,
            productPackageId: item?.productPackageId ?? '',
            userGroupId: item?.userGroupId || undefined,
            userId: item?.userId || undefined,
            licenseId: item?.licenseId || undefined,
            startDate,
            endDate: endDate || undefined,
        };

        if (Object.keys(currentConditionValues).length > 0) {
            base.conditions = Object.entries(currentConditionValues).map(([conditionKey, parameters]) => ({
                conditionKey,
                parameters,
            }));
        }

        return { ...base, page: props.page };
    }, [item, props.tenantId, currentConditionValues, startDate, endDate]);

    const getContent = useCallback((putCallback: () => void, processing: boolean): JSX.Element =>
        <EditDrawer title='Product Package Assignment' saveButtonFunction={putCallback} processing={processing}>
            <Stack spacing={2}>
                <ReadOnlyTextField label='Product Package' value={item?.packageName} />
                {Object.keys(currentConditionValues).length > 0 && (
                    <AclConditionsEditDropDown
                        conditions={currentConditionValues}
                        onChange={setCurrentConditionValues}
                    />
                )}
                {props.tenantRequiresLicense && (
                    <ReadOnlyTextField
                        label='License'
                        value={item?.billingRecord
                            ? item.billingRecord
                            : (props.page === PackageAssignmentPage.License ? props.licenseName : undefined)}
                    />
                )}
                {props.tenantRequiresLicense && (
                    <>
                        <Stack direction='row' justifyContent='space-between' spacing={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <PalmDatePicker
                                    label='Start Date'
                                    required
                                    onChange={(value?: string) => setStartDate(value)}
                                    value={startDate}
                                />
                                <PalmDatePicker
                                    label='End Date'
                                    onChange={(value?: string) => setEndDate(value)}
                                    value={endDate}
                                />
                            </LocalizationProvider>
                        </Stack>
                        {datesValidation.hasError && (
                            <ErrorHandler component='div' display='inline' errorMessage={datesValidation.errorMessage} />
                        )}
                    </>
                )}
                {props.page === PackageAssignmentPage.UserGroup && (
                    <ReadOnlyTextField label='User Group' value={props.principalName} />
                )}
                {(props.page === PackageAssignmentPage.User || props.page === PackageAssignmentPage.UserProfile) && (
                    <ReadOnlyTextField label='User' value={props.principalName} />
                )}
                <ReadOnlyTextField label='Tenant Name' value={props.tenantName} />
                {nameInConflict && (
                    <Box component='div' display='inline' sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                        Product Package Assignment already exists.
                    </Box>
                )}
            </Stack>
        </EditDrawer>,
    [
        props,
        item,
        nameInConflict,
        currentConditionValues,
        startDate,
        endDate,
        datesValidation,
    ]);

    return {
        currentConditionValues,
        startDate,
        endDate,
        datesValidation,
        onOpenChange,
        getOptions,
        getContent,
    };
}
